import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ClipLoader from 'react-spinners/ClipLoader';
import {
  isOmniGiveEmbedCode,
  loadDonorboxOmniGiveEmbed,
  scheduleDonorboxResize,
} from '../../utils/donorboxEmbed';

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

const DonorboxForm = ({ embedCode, src, width, resizeWhenVisible = false }) => {
  const containerRef = useRef(null);
  const legacyScriptRef = useRef(null);
  const mountIdRef = useRef(0);
  const [loading, setLoading] = useState(!!src && !isOmniGiveEmbedCode(embedCode));

  const resolvedEmbedCode = isOmniGiveEmbedCode(embedCode) ? embedCode : null;
  const resolvedSrc = !resolvedEmbedCode ? src || (embedCode?.startsWith?.('http') ? embedCode : null) : null;

  useIsomorphicLayoutEffect(() => {
    if (!resolvedEmbedCode || typeof window === 'undefined') return undefined;

    const container = containerRef.current;
    if (!container) return undefined;

    const mountId = ++mountIdRef.current;
    const cleanup = loadDonorboxOmniGiveEmbed(container, resolvedEmbedCode);

    return () => {
      if (mountIdRef.current === mountId) {
        cleanup();
      }
    };
  }, [resolvedEmbedCode]);

  useEffect(() => {
    if (!resizeWhenVisible || !resolvedEmbedCode || !containerRef.current) return;
    scheduleDonorboxResize(containerRef.current);
  }, [resizeWhenVisible, resolvedEmbedCode]);

  useEffect(() => {
    if (resolvedEmbedCode || !resolvedSrc || typeof document === 'undefined') return undefined;

    const script = document.createElement('script');
    script.src = 'https://donorbox.org/widget.js';
    script.setAttribute('paypalExpress', 'true');
    script.async = true;
    legacyScriptRef.current = script;
    document.body.appendChild(script);

    return () => {
      if (legacyScriptRef.current?.parentNode) {
        legacyScriptRef.current.parentNode.removeChild(legacyScriptRef.current);
      }
    };
  }, [resolvedEmbedCode, resolvedSrc]);

  if (resolvedEmbedCode) {
    return (
      <div
        ref={containerRef}
        className="donorbox-omnigive-container"
        style={{
          position: 'relative',
          width: width ?? '400px',
          minHeight: '500px',
          height: 'auto',
          textAlign: 'center',
        }}
      />
    );
  }

  const handleIframeLoad = () => {
    setLoading(false);
  };

  return (
    <div style={{ position: 'relative', width: width ?? '400px', textAlign: 'center' }}>
      {loading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
          }}
        >
          <ClipLoader loading={loading} size={150} aria-label="Loading Spinner" data-testid="loader" />
        </div>
      )}
      <iframe
        src={resolvedSrc}
        name="donorbox"
        allow="payment"
        allowPaymentRequest
        seamless="seamless"
        frameBorder="0"
        scrolling="no"
        height="900px"
        width="100%"
        style={{
          maxWidth: '100%',
          minWidth: '250px',
          maxHeight: 'none',
          borderRadius: '8px',
          marginTop: '10px',
        }}
        onLoad={handleIframeLoad}
      />
    </div>
  );
};

export default DonorboxForm;
